import React, { useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import {
  FiAlertTriangle, FiCheckCircle, FiChevronRight, FiFileText, FiGrid, FiLayers, FiPlay, FiPlus, FiRefreshCw, FiSearch, FiShield, FiTag, FiTrash2, FiUsers, FiX,
} from "react-icons/fi";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import {
  Empty, Group, IconTile, LiveDot, Pill, SearchField, Section, Skeleton, Widget, WidgetGrid, tokens,
} from "../activity-logs/styles";
import { SegmentedControl } from "../activity-logs/components/primitives";
import { IOS, colorFor } from "../activity-logs/labels";
import {
  Bento, BentoTile, CompositionWidget, CreditsPage, DirectoryCard, HeaderLink, Notice, PeriodStats, StorageBar, SupplierGrid, Tag, TopHeadline, TopRow,
} from "../creditos/styles";
import { ScrolledTopBar, SupplierAvatar, UpdatedAgo } from "../creditos/components/parts";
import { Capsule, PrimaryCapsule, RoundButton, SelectField, Toolbar, ToolbarDivider } from "../creditos/components/ControlBar";
import { tokens as T, hairline } from "../../styles/designTokens";
import { MISS, RING_COLORS } from "../revisor/home/AnaliseDashboard";
import { bandOf, displayName } from "../revisor/home/analiseData";
import { ConfirmDelete, NewCard } from "../revisor/home/RevisorHome";
import { branchLabel, formatCount, formatPercent } from "../revisor/home/revisorData";
import { HomeBar, PARTIAL } from "./XmlSupplierDirectory";
import XmlSupplierDashboard from "./XmlSupplierDashboard";
import { CST_NAMES, batchDate, buildFocus, buildXmlScanHome } from "./xmlScanData";

/**
 * Tela inicial do XML Scan, na estrutura de Créditos (abas, KPIs, gráfico principal
 * com composição, mapa com mosaico e dois rankings), com peças do contexto.
 * Pergunta da tela: os XMLs já trazem os campos da Reforma Tributária, quem ainda
 * não emite e o que foi declarado?
 * Elementos próprios: o funil do XML recebido ao completo e o mapa de campos.
 * Regras dos dados em `xmlScanData.js`.
 */

const pctParts = (v) => formatPercent(v).replace("%", "");
const classColor = (code) => {
  const prefix = String(code).slice(0, 3);
  if (prefix === "000") return T.brand;
  if (prefix === "410") return "#6b9cf0";
  if (prefix === "200") return "#a99af0";
  return "#f2c27a";
};
const classIcon = (code) => (String(code).startsWith("410") ? <FiShield /> : <FiTag />);

// ─── Minianel dos KPIs (a API não dá série mensal para uma sparkline) ────────

export function MiniRing({ value, color }) {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true" style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
      <circle cx="15" cy="15" r="12" fill="none" stroke="rgba(118,118,128,0.18)" strokeWidth="3.5" />
      {value > 0 && <circle cx="15" cy="15" r="12" fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round" pathLength="100" strokeDasharray={`${value} 100`} />}
    </svg>
  );
}

// ─── Funil contínuo ──────────────────────────────────────────────────────────

const FunnelHead = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 14px;
  font-variant-numeric: tabular-nums;
  > div { padding: 0 10px; min-width: 0; border-left: 0.5px solid ${({ theme }) => hairline(theme, true)}; }
  > div:first-child { padding-left: 0; border-left: none; }
  .v { font-size: 1.32em; font-weight: 600; letter-spacing: -0.03em; line-height: 1.15; }
  .l { margin-top: 3px; font-size: 0.76em; color: ${({ theme }) => tokens.label2(theme)}; }
  .d { margin-top: 3px; font-size: 0.72em; color: ${({ theme }) => tokens.label3(theme)}; }
  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    row-gap: 12px;
    > div:nth-child(odd) { padding-left: 0; border-left: none; }
  }
`;

function funnelPath(values, w, h) {
  const max = Math.max(1, values[0]);
  const y = (v) => h - (v / max) * (h - 6) - 1;
  const col = w / values.length;
  const xs = values.map((_, i) => col * i + col / 2);
  let d = `M0,${y(values[0])} L${xs[0]},${y(values[0])}`;
  for (let i = 1; i < values.length; i += 1) {
    const mid = (xs[i - 1] + xs[i]) / 2;
    d += ` C${mid},${y(values[i - 1])} ${mid},${y(values[i])} ${xs[i]},${y(values[i])}`;
  }
  d += ` L${w},${y(values[values.length - 1])}`;
  return d;
}

function FunnelChart({ funnel }) {
  const W = 1000; const H = 200;
  const line = funnelPath(funnel.map((s) => s.value), W, H);
  return (
    <div style={{ position: "relative", flex: 1, minHeight: 150, marginTop: 10 }} role="img" aria-label={funnel.map((s) => `${s.label}: ${formatCount(s.value)}`).join(", ")}>
      <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ position: "absolute", inset: 0, display: "block" }}>
        <defs>
          <linearGradient id="xml-funnel" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.brand} stopOpacity="0.24" />
            <stop offset="100%" stopColor={T.brand} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={`${line} L${W},${H} L0,${H} Z`} fill="url(#xml-funnel)" />
        <path d={line} fill="none" stroke={T.brand} strokeWidth="1.75" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

// ─── Mapa de campos ──────────────────────────────────────────────────────────

export const FieldMap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;

  .line { flex: 1; display: grid; grid-template-columns: 96px minmax(0, 1fr); align-items: stretch; gap: 10px; }
  .lbl { align-self: center; font-size: 0.76em; color: ${({ theme }) => tokens.label2(theme)}; line-height: 1.25; }
  .cells { display: grid; grid-template-columns: repeat(8, minmax(0, 1fr)); gap: 4px; }
  .cell { position: relative; min-height: 34px; border-radius: 9px; overflow: hidden; background: rgba(118,118,128,0.1); cursor: help; }
  .cell .fill { position: absolute; inset: 0; background: ${T.brand}; }
  .cell .nm { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; padding: 0 2px; font-size: 0.68em; font-weight: 500; white-space: nowrap; overflow: hidden; }
  @media (max-width: 720px) {
    .line { grid-template-columns: 1fr; gap: 4px; }
    .cells { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  }
`;

const MapStats = styled(PeriodStats)`
  .l { white-space: normal; }
`;

// ─── Ranking ─────────────────────────────────────────────────────────────────

function RankCard({ title, sub, noun, rank, valueKey, delay, onSeeAll, onOpen, foot }) {
  return (
    <Widget $delay={delay} style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">{title}</div>
          <div className="w-sub">{sub}</div>
        </div>
        {rank.count > 0 && <HeaderLink type="button" onClick={onSeeAll}>Ver todos <FiChevronRight /></HeaderLink>}
      </div>
      {rank.count === 0 ? <div className="w-sub" style={{ marginTop: 16 }}>Nenhum fornecedor nesta situação.</div> : (
        <>
          <TopHeadline>
            <span className="n">{pctParts(rank.share)}<span className="pct">%</span></span>
            <span className="l">dos {noun} estão em {formatCount(rank.top.length)} de {formatCount(rank.count)} fornecedores</span>
          </TopHeadline>
          <StorageBar role="img" aria-label={`${formatPercent(rank.share)} em ${rank.top.length} fornecedores`}>
            {rank.top.map((s) => <span key={s.key} title={`${displayName(s.name)}: ${formatCount(s[valueKey])}`} style={{ flexGrow: s[valueKey], background: colorFor(s.name) }} />)}
            <span className="rest" style={{ flexGrow: rank.rest }} />
          </StorageBar>
          <div style={{ marginTop: 8 }}>
            {rank.top.map((s) => (
              <TopRow key={s.key} type="button" onClick={() => onOpen(s.raw)}>
                <span className="lead"><SupplierAvatar name={s.name} size={32} /></span>
                <span className="main">
                  <span className="title" title={s.name}>{displayName(s.name)}</span>
                  <span className="sub" title={s.cnpj}>{branchLabel(s.cnpj)} · {formatPercent(s.pct)} conformes · {formatCount(s.total)} XMLs</span>
                </span>
                <span className="end">
                  <span className="value">{formatCount(s[valueKey])}</span>
                  <span className="share">{formatPercent((s[valueKey] / Math.max(1, rank.total)) * 100)}</span>
                </span>
                <FiChevronRight className="chevron" />
              </TopRow>
            ))}
          </div>
          {foot && <div className="w-sub" style={{ marginTop: "auto", paddingTop: 12, fontWeight: 300, lineHeight: 1.5 }}>{foot}</div>}
        </>
      )}
    </Widget>
  );
}

// ─── Cards ───────────────────────────────────────────────────────────────────

const BatchCard = React.memo(function BatchCard({ batch, delay, onOpen, onDelete }) {
  const color = batch.failed ? IOS.red : RING_COLORS[bandOf(batch.pct).key];
  const label = batchDate(batch);
  return (
    <div style={{ position: "relative", display: "flex" }}>
      <DirectoryCard type="button" $delay={delay} onClick={() => onOpen(batch)} style={{ flex: 1, minWidth: 0 }}>
        <span className="head">
          <IconTile $size={40} $color={color}>{batch.failed ? <FiAlertTriangle /> : <FiFileText />}</IconTile>
          <span className="who" style={{ paddingRight: 32 }}>
            <span className="name">{label}</span>
            <span className="id" title={`${batch.id} · ${batch.fileName}`}>{batch.fileName}</span>
          </span>
        </span>
        <span className="body">
          {batch.failed ? (
            <>
              <span className="amount" style={{ color: IOS.red, fontSize: "1.05em" }}>Nenhum XML lido</span>
              <span className="meta">{formatCount(batch.unread)} de {formatCount(batch.totalXmls)} XMLs com erro</span>
            </>
          ) : (
            <>
              <span className="amount">
                {formatPercent(batch.pct)}
                <span style={{ fontSize: "0.5em", fontWeight: 500, opacity: 0.5, marginLeft: 6, letterSpacing: 0 }}>conformes</span>
              </span>
              <span className="meta">{formatCount(batch.read)} lidos de {formatCount(batch.totalXmls)} XMLs</span>
              <HomeBar role="img" aria-label={`${formatPercent(batch.pct)} conformes`}>
                <span style={{ flexGrow: batch.conformes, background: T.brand }} />
                <span style={{ flexGrow: batch.parciais, background: PARTIAL }} />
                <span style={{ flexGrow: batch.naoConformes, background: MISS }} />
              </HomeBar>
            </>
          )}
          <span className="taxes">
            {batch.suppliers.length > 0 && <Tag>{formatCount(batch.suppliers.length)} fornecedores</Tag>}
            {batch.repeated && <span title="Mesmos números de outra análise mais recente"><Tag $color={IOS.orange}>Repetida</Tag></span>}
          </span>
        </span>
      </DirectoryCard>
      <RoundButton
        type="button"
        title="Excluir análise"
        aria-label={`Excluir análise de ${label}`}
        onClick={() => onDelete(batch.operationId, label)}
        style={{ position: "absolute", top: 12, right: 12, width: 30, height: 30, fontSize: 13 }}
      >
        <FiTrash2 />
      </RoundButton>
    </div>
  );
});

// ─── Tela ────────────────────────────────────────────────────────────────────

export default function XmlScanHome({
  batches: rawBatches, loading, failed, scanning, updatedAt, onRefresh, onCreate, onOpenBatch, onOpenSupplier,
  onAskDelete, deleteConfirm, onCancelDelete, onConfirmDelete,
}) {
  const theme = useTheme();
  const data = useMemo(() => buildXmlScanHome(rawBatches), [rawBatches]);
  const [tab, setTab] = useState("overview");
  const [selectedId, setSelectedId] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(null);
  const [extra, setExtra] = useState(null);

  const selected = data.selectable.find((x) => x.id === selectedId) || data.selectable[0] || null;
  const f = useMemo(() => (selected ? buildFocus(selected) : null), [selected]);
  const b = f?.batch;
  const firstLoad = loading && data.batches.length === 0;

  const goSuppliers = (nextFilter = null, nextExtra = null) => {
    setFilter(nextFilter);
    setExtra(nextExtra);
    setQ("");
    setTab("suppliers");
  };

  const batchList = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data.batches;
    return data.batches.filter((x) => String(x.id).toLowerCase().includes(term) || String(x.fileName).toLowerCase().includes(term) || batchDate(x).includes(term));
  }, [data.batches, q]);

  const result = b ? [
    { key: "c", label: "Conformes", value: b.conformes, color: T.brand },
    { key: "p", label: "Parciais", value: b.parciais, color: PARTIAL },
    { key: "n", label: "Não conformes", value: b.naoConformes, color: MISS },
  ].filter((x) => x.value > 0) : [];

  const classRows = f ? f.classes.slice(0, 5) : [];
  const classSum = f ? f.classes.reduce((s, c) => s + c.value, 0) : 0;
  const [firstClass, secondClass, ...restClass] = classRows;
  const normal = f?.finalities.find((x) => x.code === "1");
  const devol = f?.finalities.find((x) => x.code === "4");

  const ClassTile = ({ row, size }) => (
    <BentoTile
      type="button"
      $color={classColor(row.code)}
      $size={size}
      title={`cClassTrib ${row.code}: ${formatCount(row.value)}. Ver os fornecedores`}
      onClick={() => goSuppliers(null, { label: `cClassTrib ${row.code}`, test: (s) => (s.raw.cClassTribUsados?.[row.code] || 0) > 0 })}
    >
      <span className="top">
        <span className="pct">{row.share > 0 && row.share < 0.1 ? "< 0,1" : pctParts(row.share)}<small>%</small></span>
        <span className="icon">{classIcon(row.code)}</span>
      </span>
      <span className="bottom">
        <span className="label">{size === "small" ? row.code : `${CST_NAMES[String(row.code).slice(0, 3)] || "cClassTrib"}`}</span>
        <span className="amount">{size === "small" ? formatCount(row.value) : `${row.code} · ${formatCount(row.value)}`}</span>
        <span className="track"><span className="level" style={{ width: `${row.share}%`, background: classColor(row.code) }} /></span>
      </span>
    </BentoTile>
  );

  return (
    <>
      <CreditsPage>
        <ScrolledTopBar deps={[tab]}>
          <div className="title-row">
            <div>
              <div className="eyebrow">Vidal · Reforma Tributária</div>
              <h1>XML Scan</h1>
              <div className="meta">
                {scanning ? <Pill $color={IOS.orange}>Analisando XMLs</Pill> : failed ? <Pill $color={IOS.red}>Falha ao carregar</Pill> : <LiveDot>Dados da API</LiveDot>}
                {b && <span className="update-tag">{formatCount(b.totalXmls)} XMLs · {formatCount(f.supplierCount)} fornecedores</span>}
                {updatedAt && <UpdatedAgo at={updatedAt} />}
              </div>
            </div>
          </div>
          <div className="nav-row">
            <SegmentedControl
              value={tab}
              onChange={(v) => { setTab(v); setQ(""); }}
              options={[
                { key: "overview", icon: <FiGrid />, label: "Visão geral" },
                { key: "suppliers", icon: <FiUsers />, label: "Fornecedores", count: f ? f.supplierCount : undefined },
                { key: "batches", icon: <FiLayers />, label: "Análises", count: data.batches.length },
              ]}
            />
            <Toolbar>
              {data.selectable.length > 0 && (
                <SelectField icon={<FiFileText />} label="Análise" value={selected?.id || ""} onChange={setSelectedId}>
                  {data.selectable.map((x) => <option key={x.id} value={x.id}>Análise de {batchDate(x, false)}</option>)}
                </SelectField>
              )}
              {selected && <Capsule type="button" onClick={() => onOpenBatch(selected.raw)}>Abrir análise <FiChevronRight /></Capsule>}
              <ToolbarDivider />
              <RoundButton type="button" onClick={onRefresh} title="Atualizar" aria-label="Atualizar">
                <FiRefreshCw className={loading ? "spin" : ""} />
              </RoundButton>
              <PrimaryCapsule type="button" onClick={onCreate} disabled={scanning}>
                {scanning ? <FiRefreshCw className="spin" /> : <FiPlay />} {scanning ? "Analisando…" : "Nova análise"}
              </PrimaryCapsule>
            </Toolbar>
          </div>
        </ScrolledTopBar>

        {firstLoad && (
          <Section $first>
            <WidgetGrid>{[0, 1, 2, 3].map((i) => <Widget key={i}><Skeleton $h={88} $r={12} /></Widget>)}</WidgetGrid>
            <WidgetGrid style={{ marginTop: 16 }}>
              <Widget $span={3}><Skeleton $h={260} $r={14} /></Widget>
              <Widget><Skeleton $h={260} $r={14} /></Widget>
            </WidgetGrid>
          </Section>
        )}

        {!loading && failed && data.batches.length === 0 && (
          <Section $first>
            <Group>
              <Empty>
                <div className="e-icon"><FiAlertTriangle /></div>
                <div className="e-title">Não foi possível carregar as análises</div>
                <div className="e-sub" style={{ marginBottom: 14 }}>Verifique a conexão e tente de novo.</div>
                <Capsule type="button" onClick={onRefresh} style={{ margin: "0 auto" }}>Tentar de novo</Capsule>
              </Empty>
            </Group>
          </Section>
        )}

        {!loading && !failed && data.batches.length === 0 && (
          <Section $first>
            <Group>
              <Empty>
                <div className="e-icon"><FiFileText /></div>
                <div className="e-title">Nenhuma análise ainda</div>
                <div className="e-sub" style={{ marginBottom: 14 }}>Envie um ZIP com os XMLs para ver quais já trazem os campos da Reforma Tributária.</div>
                <PrimaryCapsule type="button" onClick={onCreate} disabled={scanning} style={{ margin: "0 auto" }}><FiPlay /> Iniciar a primeira análise</PrimaryCapsule>
              </Empty>
            </Group>
          </Section>
        )}

        {tab === "overview" && data.recentFailed.length > 0 && (
          <Notice style={{ marginTop: 16 }}>
            <FiAlertTriangle style={{ color: IOS.red, opacity: 1 }} />
            <span>
              {data.recentFailed.length === 1 ? "A análise mais recente não leu nenhum XML" : `As ${formatCount(data.recentFailed.length)} análises mais recentes não leram nenhum XML`}
              {b ? `. Aqui está a análise de ${batchDate(b, false)}.` : "."}
            </span>
          </Notice>
        )}

        {tab === "overview" && f && (
          <>
            {/* KPIs */}
            <Section $first={data.recentFailed.length === 0}>
              <WidgetGrid>
                <Widget $delay={0} $clickable onClick={() => setTab("batches")}>
                  <div className="w-head"><FiFileText style={{ color: "#6b9cf0", opacity: 1 }} /> XMLs lidos</div>
                  <div className="w-value">{formatCount(b.read)}</div>
                  <div className="w-foot" style={{ justifyContent: "space-between" }}>
                    <span>de {formatCount(b.totalXmls)} recebidos</span>
                    <MiniRing value={b.totalXmls > 0 ? (b.read / b.totalXmls) * 100 : 0} color="#6b9cf0" />
                  </div>
                </Widget>
                <Widget $delay={50} $clickable onClick={() => goSuppliers("conformes")}>
                  <div className="w-head"><FiCheckCircle style={{ color: T.brand, opacity: 1 }} /> Conformes</div>
                  <div className="w-value">{formatCount(b.conformes)}</div>
                  <div className="w-foot" style={{ justifyContent: "space-between" }}>
                    <span>{formatPercent(b.pct)} dos lidos</span>
                    <MiniRing value={b.pct} color={T.brand} />
                  </div>
                </Widget>
                <Widget $delay={100} $clickable onClick={() => goSuppliers("parciais")}>
                  <div className="w-head"><span style={{ width: 7, height: 7, borderRadius: "50%", background: PARTIAL, opacity: 1 }} /> Parciais</div>
                  <div className="w-value">{formatCount(b.parciais)}</div>
                  <div className="w-foot" style={{ justifyContent: "space-between" }}>
                    <span>{formatPercent(b.read > 0 ? (b.parciais / b.read) * 100 : 0)} dos lidos</span>
                    <MiniRing value={b.read > 0 ? (b.parciais / b.read) * 100 : 0} color={PARTIAL} />
                  </div>
                </Widget>
                <Widget $delay={150} $clickable onClick={() => goSuppliers("naoConformes")}>
                  <div className="w-head"><span style={{ width: 7, height: 7, borderRadius: "50%", background: MISS, opacity: 1 }} /> Não conformes</div>
                  <div className="w-value">{formatCount(b.naoConformes)}</div>
                  <div className="w-foot" style={{ justifyContent: "space-between" }}>
                    <span>{formatPercent(b.read > 0 ? (b.naoConformes / b.read) * 100 : 0)} dos lidos</span>
                    <MiniRing value={b.read > 0 ? (b.naoConformes / b.read) * 100 : 0} color={MISS} />
                  </div>
                </Widget>
              </WidgetGrid>
            </Section>

            {/* Funil + composição */}
            <Section $delay={80}>
              <WidgetGrid>
                <Widget $span={3} $delay={200} style={{ display: "flex", flexDirection: "column" }}>
                  <div className="w-title">Do XML recebido ao XML completo</div>
                  <div className="w-sub">
                    {formatCount(b.conformes)} dos {formatCount(b.totalXmls)} XMLs chegam ao fim com todos os campos de IBS e CBS. O gráfico mostra onde os outros param.
                  </div>
                  <FunnelHead>
                    {f.funnel.map((s) => (
                      <div key={s.key}>
                        <div className="v">{formatCount(s.value)}</div>
                        <div className="l">{s.label}</div>
                        <div className="d">{s.drop > 0 ? `−${formatCount(s.drop)} ${s.dropLabel}` : " "}</div>
                      </div>
                    ))}
                  </FunnelHead>
                  <FunnelChart funnel={f.funnel} />
                </Widget>

                <CompositionWidget $delay={250}>
                  <div className="w-title">Resultado</div>
                  <div className="w-sub">Dos XMLs lidos</div>
                  <div style={{ position: "relative", height: 150, marginTop: 12 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={result} dataKey="value" nameKey="label" innerRadius={56} outerRadius={68} cornerRadius={4} paddingAngle={result.length > 1 ? 3 : 0} stroke="none" startAngle={90} endAngle={-270} isAnimationActive={false}>
                          {result.map((x) => <Cell key={x.key} fill={x.color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                      <b style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.03em" }}>{pctParts(b.pct)}<span style={{ fontSize: 13, opacity: 0.5, fontWeight: 600 }}>%</span></b>
                      <span style={{ fontSize: 11, color: tokens.label2(theme) }}>conformes</span>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 7 }}>
                    {result.map((x) => (
                      <div key={x.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: "0.82em" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 7 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: x.color }} />{x.label}</span>
                        <span style={{ color: tokens.label2(theme), fontVariantNumeric: "tabular-nums" }}>{formatPercent((x.value / b.read) * 100)}</span>
                      </div>
                    ))}
                  </div>
                </CompositionWidget>
              </WidgetGrid>
            </Section>

            {/* Mapa de campos + mosaico */}
            <Section $delay={140}>
              <WidgetGrid>
                <Widget $span={2} $delay={300} style={{ display: "flex", flexDirection: "column" }}>
                  <div className="w-title">Mapa de campos</div>
                  <div className="w-sub">Quanto mais verde, mais XMLs de 2026 em diante trazem o campo. Passe o mouse para ver cada um</div>
                  <FieldMap>
                    {f.groups.map((g) => (
                      <div className="line" key={g.key}>
                        <span className="lbl">{g.label}</span>
                        <div className="cells">
                          {g.fields.map((x) => (
                            <div className="cell" key={x.field} title={`${x.desc}: em ${formatPercent(x.pct)} dos XMLs, ausente em ${formatCount(x.absent)}`}>
                              {x.pct > 0 && <span className="fill" style={{ opacity: 0.12 + (x.pct / 100) * 0.68 }} />}
                              <span className="nm">{x.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </FieldMap>
                  <MapStats style={{ marginTop: 14 }}>
                    {f.groups.map((g) => (
                      <div key={g.key}>
                        <span className="v">{formatPercent(g.pct)}</span>
                        <span className="l">{g.label}{g.optional ? " (opcional)" : ""}</span>
                      </div>
                    ))}
                  </MapStats>
                </Widget>

                <Widget $span={2} $delay={350} style={{ display: "flex", flexDirection: "column" }}>
                  <div className="w-title">Por classificação tributária</div>
                  <div className="w-sub">
                    {formatCount(f.classes.length)} {f.classes.length === 1 ? "código" : "códigos"} de cClassTrib
                    {normal ? ` · ${formatPercent(normal.share)} notas normais` : ""}{devol ? `, ${formatCount(devol.value)} devoluções` : ""}. Clique para ver os fornecedores
                  </div>
                  {firstClass ? (
                    <Bento>
                      <ClassTile row={firstClass} size="large" />
                      {secondClass && (
                        <div className="side">
                          <ClassTile row={secondClass} size="medium" />
                          {restClass.length > 0 && <div className="base">{restClass.map((r) => <ClassTile key={r.code} row={r} size="small" />)}</div>}
                        </div>
                      )}
                    </Bento>
                  ) : <div className="w-sub" style={{ marginTop: 14 }}>Nenhuma classificação declarada nesta análise.</div>}
                  {classSum > 0 && f.classes.length > 5 && <div className="w-foot" style={{ marginTop: 8 }}>E mais {formatCount(f.classes.length - 5)} códigos com {formatCount(f.classes.slice(5).reduce((s, c) => s + c.value, 0))} declarações.</div>}
                </Widget>
              </WidgetGrid>
            </Section>

            {/* Rankings */}
            <Section $delay={200}>
              <WidgetGrid $cols={2}>
                <RankCard
                  title="Sem o grupo IBS/CBS"
                  sub="Quem ainda não emite com os campos da Reforma"
                  noun="não conformes"
                  rank={f.rankings.naoConformes}
                  valueKey="naoConformes"
                  delay={400}
                  onSeeAll={() => goSuppliers("naoConformes")}
                  onOpen={(s) => onOpenSupplier(b.raw, s)}
                />
                <RankCard
                  title="Sem base e valores"
                  sub="Quem declara CST e classificação, mas não os valores"
                  noun="parciais"
                  rank={f.rankings.parciais}
                  valueKey="parciais"
                  delay={450}
                  onSeeAll={() => goSuppliers("parciais")}
                  onOpen={(s) => onOpenSupplier(b.raw, s)}
                  foot={f.parcial410 > 0 && f.parcial410 >= b.parciais / 2
                    ? `${formatCount(f.parcial410)} dos ${formatCount(b.parciais)} parciais podem ser CST 410 (imunidade e não incidência), que normalmente não leva base nem valor. Vale o contador confirmar antes de tratar como pendência.`
                    : null}
                />
              </WidgetGrid>
            </Section>
          </>
        )}

        {tab === "suppliers" && f && (
          <XmlSupplierDashboard
            key={selected?.id}
            suppliers={f.suppliers}
            filter={filter}
            onFilter={setFilter}
            extra={extra}
            onExtra={setExtra}
            onOpen={(raw) => onOpenSupplier(b.raw, raw)}
          />
        )}

        {tab === "batches" && data.batches.length > 0 && (
          <Section $first>
            <SearchField $max="320px">
              <FiSearch />
              <input placeholder="Buscar por data, lote ou arquivo" aria-label="Buscar análise" value={q} onChange={(e) => setQ(e.target.value)} />
              {q && <button className="clear" aria-label="Limpar busca" onClick={() => setQ("")}><FiX /></button>}
            </SearchField>
            <SupplierGrid style={{ marginTop: 14 }}>
              {!q && (
                <NewCard type="button" onClick={onCreate} disabled={scanning}>
                  <span className="plus"><FiPlus /></span>
                  <span className="t">Nova análise</span>
                  <span className="s">Envie um ZIP com os XMLs</span>
                </NewCard>
              )}
              {batchList.map((x, i) => <BatchCard key={x.id} batch={x} delay={Math.min(i, 11) * 25} onOpen={(bt) => onOpenBatch(bt.raw)} onDelete={onAskDelete} />)}
            </SupplierGrid>
            {batchList.length === 0 && (
              <Group style={{ marginTop: 14 }}>
                <Empty>
                  <div className="e-icon"><FiSearch /></div>
                  <div className="e-title">Nenhuma análise</div>
                  <div className="e-sub">Nada encontrado para "{q}".</div>
                </Empty>
              </Group>
            )}
          </Section>
        )}
      </CreditsPage>

      {deleteConfirm && <ConfirmDelete name={deleteConfirm.batchName} onCancel={onCancelDelete} onConfirm={onConfirmDelete} />}
    </>
  );
}
